import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetConversationIdQuery,
  useGetMessagesQuery,
  useGetNewMessagesQuery,
  useSendMessageMutation,
} from 'services/chatService';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getTimestamp, longFormatDate } from 'utils';
import { PendoEventType } from 'features/pendo/types';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { Message, MessageGroupData } from '../types';
import { useUnreadMessagesCount } from './useUnreadMessagesCount';

const POLLING_INTERVAL = 5000;

// Helper method to create an unsent chat message
const createUnsentMessage = (message: string): Message => ({
  id: Math.floor(Math.random() * 1000000) + Date.now(), // Use a unique ID for unsent messages
  messageText: message,
  messageType: 'APP',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  authorId: -1,
  authorType: 'PROGRAM_USER',
  firstName: 'You',
  lastName: '',
  status: 'SENDING',
  uuid: '',
  groupMessage: false,
});

export const useChatMessageData = () => {
  // Fetching individual pages
  const [page, setPage] = useState(0);
  const [loadMoreMessagesError, setLoadMoreMessagesError] = useState(false);
  const user = useLoggedInUser();
  const { translation, currentLocale } = useTranslationContext();
  const userId = user?.id;
  const {
    data: conversation,
    error: isConversationIdError,
    isLoading: isConversationIdLoading,
  } = useGetConversationIdQuery(userId ? { userId } : skipToken);

  const { trackEvent } = usePendo();

  const conversationData = useMemo(() => {
    if (conversation) return { conversationId: conversation.id, pageNumber: page };
    return skipToken;
  }, [conversation, page]);

  // Keep track of unsent messages in local state to display them in the UI
  const [unsentMessages, setUnsentMessages] = useState<Message[]>([]);
  const [sendMessage, { isLoading: isMessageSending }] = useSendMessageMutation();

  const updateUnsentMessageStatus = useCallback(
    (
      messageId: number,
      status: 'FAILED_TO_SEND' | 'ACTIVE' | 'SENDING',
      serverMessage?: Message,
    ) => {
      setUnsentMessages((prev) => {
        if (status === 'ACTIVE' && serverMessage) {
          // Update with real server message ID and mark as ACTIVE
          return prev.map((msg) =>
            msg.id === messageId ? { ...msg, id: serverMessage.id, status } : msg,
          );
        }
        return prev.map((msg) => (msg.id === messageId ? { ...msg, status } : msg));
      });
    },
    [],
  );

  const handleSendMessage = async (message: string) => {
    if (conversation) {
      const unsentMessage = createUnsentMessage(message);
      setUnsentMessages((messages) => [...messages, unsentMessage]);
      const result = await sendMessage({ conversationId: conversation.id, message });
      if (result.error) {
        console.error('Error sending message:', result.error);
        updateUnsentMessageStatus(unsentMessage.id, 'FAILED_TO_SEND');
      } else {
        // Update the unsent message with the actual ID from the server and mark it as ACTIVE
        updateUnsentMessageStatus(unsentMessage.id, 'ACTIVE', result.data);
        trackEvent(PendoEventType.CHAT_MESSAGE_SENT, {
          eventType: PendoEventType.CHAT_MESSAGE_SENT,
          messageId: `${result.data.id}`,
        });
      }
    }
  };

  const handleResendMessage = async (message: Message) => {
    if (message.status !== 'FAILED_TO_SEND') return;
    updateUnsentMessageStatus(message.id, 'SENDING');

    const result = await sendMessage({
      conversationId: conversation!.id,
      message: message.messageText || '',
    });

    if (result.error) {
      console.error('Error resending message:', result.error);
      updateUnsentMessageStatus(message.id, 'FAILED_TO_SEND');
    } else {
      // Update the unsent message with the actual ID from the server and mark it as ACTIVE
      updateUnsentMessageStatus(message.id, 'ACTIVE', result.data);
      trackEvent(PendoEventType.CHAT_MESSAGE_SENT, {
        eventType: PendoEventType.CHAT_MESSAGE_SENT,
        messageId: `${result.data.id}`,
      });
    }
  };

  // Polling for new messages
  const pollingConversationData = conversation
    ? { conversationId: conversation.id, pageNumber: 0 }
    : skipToken;
  const {
    error: isNewMessageDataError,
    data: messageData,
    isLoading: isNewMessageLoading,
    refetch: refetchNewMessageData,
  } = useGetNewMessagesQuery(pollingConversationData, {
    pollingInterval: POLLING_INTERVAL,
    skipPollingIfUnfocused: true,
  });

  const {
    error: isMergedMessageDataError,
    isLoading: isMergedMessageDataLoading,
    isFetching: isMergedMessageDataFetching,
    refetch: refetchMergedMessageData,
  } = useGetMessagesQuery(conversationData, {
    skip: page === 0,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isMergedMessageDataError) {
      setLoadMoreMessagesError(true);
    }
  }, [isMergedMessageDataError]);

  const resetLoadMoreMessagesError = () => {
    setLoadMoreMessagesError(false);
  };

  const isChatLoading =
    isConversationIdLoading ||
    isMergedMessageDataLoading ||
    isMergedMessageDataFetching ||
    isNewMessageLoading;

  const isChatError = isConversationIdError || isNewMessageDataError;

  const groupMessagesByDateAndTimestamp = (messages: Message[]): MessageGroupData => {
    const groupedMessages: MessageGroupData = {};

    messages
      // Filter out messages without createdAt and updatedAt
      .filter(({ createdAt, updatedAt }) => createdAt && updatedAt)
      .forEach(({ createdAt, updatedAt, authorType, ...message }) => {
        const formattedDate = longFormatDate(createdAt);
        const timestamp = getTimestamp(updatedAt, currentLocale);

        // Initialize grouping for date and timestamp
        groupedMessages[formattedDate] ??= {};
        groupedMessages[formattedDate][timestamp] ??= [];

        // Push message to the group
        groupedMessages[formattedDate][timestamp].push({
          createdAt,
          updatedAt,
          authorType,
          ...message,
        });
      });

    return groupedMessages;
  };

  const loadMoreMessages = () => {
    // If there was an error loading the merged message before, refetch it
    if (isMergedMessageDataError) {
      refetchMergedMessageData();
      return;
    }

    // Otherwise, load more messages if there are more available
    if (messageData?.hasMore && !isMergedMessageDataLoading) {
      setPage((n) => n + 1);
    }
  };

  const { hasUnreadMessages, unreadMessagesCount } = useUnreadMessagesCount(userId, messageData);
  const onRetryAction = useCallback(() => {
    refetchNewMessageData();
  }, [refetchNewMessageData]);
  const serverErrorHeading = translation('server_error_on_checklist_headline');
  const serverErrorBody = translation('server_error_on_checklist_body');

  const userFullName = useMemo(() => {
    if (user?.first_name || user?.last_name) {
      const firstName = user?.first_name || '';
      const lastName = user?.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName;
    }
    return 'You';
  }, [user?.first_name, user?.last_name]);

  return {
    conversationId: conversation?.id,
    groupMessagesByDateAndTimestamp,
    messageData,
    unsentMessages,
    isChatLoading,
    isChatError,
    loadMoreMessagesError,
    resetLoadMoreMessagesError,
    loadMoreMessages,
    handleSendMessage,
    handleResendMessage,
    hasUnreadMessages,
    unreadMessagesCount,
    onRetryAction,
    serverErrorHeading,
    serverErrorBody,
    userFullName,
  };
};
